import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, ArrowClockwise } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Proactive re-engagement nudge for listings nearing expiration or already expired.
 * Renders nothing if the user has zero expiring/expired listings.
 *
 * @param {'card'|'inline'} variant  'card' = full amber callout (MyListingsPage). 'inline' = compact pill row (UserMenuPanel)
 * @param {Function} [onNavigate]    optional click handler (used by UserMenuPanel to close its dropdown)
 */
export const ExpirationNudge = ({ variant = 'card', onNavigate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/listings/me/expiring-summary`, { withCredentials: true });
        if (!cancelled) setSummary(data);
      } catch {
        /* silent — the nudge is optional UX, never blocking */
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!summary) return null;
  const { expiring_soon_count: expiring = 0, expired_count: expired = 0 } = summary;
  if (expiring === 0 && expired === 0) return null;

  const primaryText = expiring > 0
    ? t('listing.expiringNudge', { count: expiring })
    : t('listing.expiredNudge', { count: expired });

  // Inline variant: used inside UserMenuPanel — compact, single row, amber dot
  if (variant === 'inline') {
    const target = expiring > 0 ? '/my-listings?filter=expiring' : '/my-listings?filter=expired';
    return (
      <Link
        to={target}
        onClick={onNavigate}
        className="flex items-center gap-2 px-3 py-2 mx-2 my-1 rounded-md border border-warning bg-warning-soft hover:bg-warning-soft transition-colors"
        data-testid="expiration-nudge-inline"
      >
        <Clock size={16} weight="bold" className="text-warning flex-shrink-0" />
        <span className="text-xs font-medium text-warning truncate">{primaryText}</span>
      </Link>
    );
  }

  // Card variant: full callout above the MyListings grid
  return (
    <div
      className="mb-5 border border-warning bg-warning-soft rounded-xl p-4 flex items-start gap-3"
      data-testid="expiration-nudge-card"
      role="status"
    >
      <div className="w-9 h-9 rounded-full bg-warning-soft flex items-center justify-center flex-shrink-0">
        <Clock size={18} weight="bold" className="text-warning" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-warning" data-testid="expiration-nudge-title">
          {primaryText}
        </p>
        {expiring > 0 && expired > 0 && (
          <p className="text-xs text-warning mt-0.5" data-testid="expiration-nudge-subtext">
            {t('listing.expiredNudge', { count: expired })}
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {expiring > 0 && (
            <Link
              to="/my-listings?filter=expiring"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-warning text-warning hover:bg-warning-soft transition-colors"
              data-testid="expiration-nudge-view-btn"
            >
              {t('listing.viewListings')}
            </Link>
          )}
          {expired > 0 && (
            <Link
              to="/my-listings?filter=expired"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-success text-white hover:bg-success/90 transition-colors"
              data-testid="expiration-nudge-repost-btn"
            >
              <ArrowClockwise size={14} weight="bold" />
              {t('listing.repost')}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
